import React, { useEffect, useRef, useState } from 'react'
import { PackageDetail } from './PackageDetail'
import { Button } from '../../../components/shadcn/Button'
import { FormProvider, useFieldArray, useForm } from 'react-hook-form'
import { OrderItemsTable, OrderItemsTable_OrderItemFragment } from '../OrderItemsTable'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowsUpDownLeftRight, faRotateRight, faTrash, faXmark, faBoxOpen } from '@fortawesome/pro-solid-svg-icons'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useOrderDetailContext } from '../OrderDetailContext'
import { compareObjectPrimitives } from '../../../lib/Util'
import { toastPromise, handlePromiseAllToast, toastError } from '../../../lib/toastHelpers'
import { graphql, useFragment } from '../../../gql'
import { useGQLFetcher } from '../../../context/GQLClientProvider'
import { useBasicEntities } from '../../../context/BasicEntitiesContext'
import { order } from '../../../gql/graphql'
import { FragmentValue } from '../../../lib/RequestUtils'

type Props = {
  orderId: number
}

export interface FromPackage {
  packageId: number
  items: number[]
  code: string
  carrier: string
  tariff: number
  weight: number
  dimensionX: number
  dimensionY: number
  dimensionZ: number
}

export interface FromData {
  packages: {
    packageId: number
    items: number[]
  }[]
}

type PackageFormData = {
  [packageId: number]: Omit<FromPackage, 'packageId' | 'items' | 'code' | 'carrier'>
}

const createNewPackageMutation = graphql(`
  mutation CreateNewPackage(
    $order: Int!
    $items: [Int!]!
    $tariff: Int
    $weight: Float
    $carrier: String!
    $dimensionX: Float
    $dimensionY: Float
    $dimensionZ: Float
  ) {
    createPackage(
      order: $order
      items: $items
      tariff: $tariff
      weight: $weight
      carrier: $carrier
      dimensionX: $dimensionX
      dimensionY: $dimensionY
      dimensionZ: $dimensionZ
    ) {
      errors
      data {
        id
        code
        sent
        carrier {
          id
          name
          tariffs {
            id
            name
          }
        }
        tariff {
          id
          name
        }
        weight
        dimensionX
        dimensionY
        dimensionZ
        items {
          id
          type
        }
      }
    }
  }
`)

const deletePackageMutation = graphql(`
  mutation DeletePackage($id: Int!) {
    deletePackage(id: $id) {
      errors
    }
  }
`)

const updatePackageMutation = graphql(`
  mutation UpdatePackage(
    $id: Int!
    $items: [Int!]!
    $tariff: Int = null
    $weight: Float = null
    $dimensionX: Float = null
    $dimensionY: Float = null
    $dimensionZ: Float = null
  ) {
    updatePackage(
      id: $id
      items: $items
      tariff: $tariff
      weight: $weight
      dimensionX: $dimensionX
      dimensionY: $dimensionY
      dimensionZ: $dimensionZ
    ) {
      errors
      data {
        id
        code
        sent
        carrier {
          id
          name
          tariffs {
            id
            name
          }
        }
        tariff {
          id
          name
        }
        weight
        dimensionX
        dimensionY
        dimensionZ
        items {
          id
          type
        }
      }
    }
  }
`)

const resetPackageMutation = graphql(`
  mutation ResetPackage($id: Int!) {
    resetPackage(id: $id) {
      errors
    }
  }
`)

export const OrderItemsInPackages_OrderFragment = graphql(`
  fragment OrderItemsInPackages_OrderFragment on Order {
    id
    carrier {
      id
      name
      tariffs {
        id
        name
      }
    }
    processStatus {
      id
    }
    shippingAddress {
      id
    }
    items {
      id
      type
      packed
      ...OrderItemsTable_OrderItemFragment
    }
    packages {
      id
      code
      sent
      carrier {
        id
        name
        tariffs {
          id
          name
        }
      }
      tariff {
        id
        name
      }
      weight
      dimensionX
      dimensionY
      dimensionZ
      items {
        id
        type
      }
    }
  }
`)

export const OrderItemsInPackages: React.FC<Props> = ({ orderId }) => {
  const [movingOrderItemIds, setMovingOrderItemIds] = useState<number[]>([])
  const [packageForms, setPackageForms] = useState<PackageFormData>({})
  const [newPackageCounter, setNewPackageCounter] = useState(0)
  const hasTriggeredAddInitialPackage = useRef(false)
  const { carriers } = useBasicEntities()
  const queryClient = useQueryClient()
  const context = useOrderDetailContext()
  const currentOrder = useFragment(OrderItemsInPackages_OrderFragment, context.order)
  const packages = currentOrder.packages || []

  const resetPackageQuery = useGQLFetcher(resetPackageMutation)

  const form = useForm<FromData>({
    defaultValues: {
      packages: packages.map(p => ({
        packageId: p?.id,
        items: p?.items.map(oi => oi.id),
      })),
    },
  })

  const { control, setValue, handleSubmit, watch, reset } = form

  useEffect(() => {
    reset({
      packages: packages.map(p => ({
        packageId: p?.id,
        items: p?.items.map(oi => oi?.id),
      })),
    })

    // Reset package forms
    const newPackageForms: PackageFormData = {}
    packages.forEach(p => {
      newPackageForms[p.id] = {
        tariff: p?.tariff?.id || null,
        weight: p?.weight || null,
        dimensionX: p?.dimensionX || null,
        dimensionY: p?.dimensionY || null,
        dimensionZ: p?.dimensionZ || null,
      }
    })
    setPackageForms(newPackageForms)
  }, [packages])

  const packagesForm = watch('packages')

  const { fields, append } = useFieldArray({
    control,
    name: 'packages',
  })

  const handlePackageChange = (
    packageId: number,
    data: Omit<FromPackage, 'packageId' | 'items' | 'code' | 'carrier'>,
  ) => {
    setPackageForms(prev => ({
      ...prev,
      [packageId]: data,
    }))
  }

  const [isSaving, setIsSaving] = useState(false)

  // The logic is too complex to be in this component body, so it was moved to a separate hook below the component
  const { mutate: savePackages, isError } = useSavePackages({
    packageForms,
    orderId,
    onSavingChange: setIsSaving,
  })

  const addPackage = (save: boolean = true) => {
    const orderItemsInPackages: number[] = packages
      .flatMap(p => p.items.filter(oi => oi.type === 'product'))
      .map(oi => oi.id)
    const items = currentOrder.items.filter(oi => !orderItemsInPackages.includes(oi.id)).map(oi => oi.id)

    const newPackageId = -(newPackageCounter + 1)
    setNewPackageCounter(prev => prev + 1)

    append({
      packageId: newPackageId,
      items: items,
    })

    // Initialize form data for the new package
    setPackageForms(prev => ({
      ...prev,
      [newPackageId]: {
        tariff: currentOrder?.carrier?.tariffs?.[0]?.id || null,
        carrier: currentOrder?.carrier?.id || carriers[0]?.id,
        weight: 0,
        dimensionX: 0,
        dimensionY: 0,
        dimensionZ: 0,
      },
    }))

    if (save) {
      savePackages(form.getValues())
    }
  }

  // preventing double creation from query refetches / race conditions.
  useEffect(() => {
    if (packages.length === 0 && !isError && !hasTriggeredAddInitialPackage.current) {
      hasTriggeredAddInitialPackage.current = true
      addPackage()
    }
  }, [packages, isError])

  const addPackages = async () => {
    const quantity = Number(prompt('Kolik?', '2'))
    if (isNaN(quantity)) {
      alert('Zadejte platné číslo')
      return
    }
    const q = new Array(quantity)
    for (const _ of q) {
      addPackage(false)
    }
    savePackages(form.getValues())
  }

  const resetPackage = async (packId: number) => {
    await toastPromise(resetPackageQuery({ id: packId }), {
      pending: 'Resetování balíku...',
      success: 'Balík byl úspěšně resetován',
      error: 'Chyba při resetování balíku',
    })
    queryClient.invalidateQueries({ queryKey: ['order', { id: orderId }] })
  }

  const handleMovePressed = (pack: FromPackage) => () => {
    setValue(
      'packages',
      fields.map(f =>
        f.packageId === pack.packageId
          ? {
              ...f,
              items: [...f.items, ...movingOrderItemIds],
            }
          : {
              ...f,
              items: f.items.filter(el => !movingOrderItemIds.includes(el)),
            },
      ),
    )
    setMovingOrderItemIds([])
    handleSubmit(d => savePackages(d))()
  }

  const renderActions = (item: FragmentValue<typeof OrderItemsTable_OrderItemFragment>) => {
    const isSelected = movingOrderItemIds.includes(item.id)

    if (isSelected) {
      return (
        <div
          className="bg-orange rounded-xl px-3 py-2 text-white flex items-center gap-2 text-md cursor-pointer select-none"
          onClick={() => setMovingOrderItemIds(prev => prev.filter(id => id !== item.id))}>
          <FontAwesomeIcon icon={faXmark} size="lg" className="text-white" />
          Zrušit
        </div>
      )
    }
    if (packagesForm.length > 1) {
      return (
        <div
          className="border-orange border-[1px] text-orange bg-white rounded-xl px-3 py-2 flex items-center gap-2 text-md cursor-pointer select-none"
          onClick={() => setMovingOrderItemIds(prev => [...prev, item.id])}>
          <FontAwesomeIcon icon={faArrowsUpDownLeftRight} size="lg" className="text-orange" />
          Přesunout
        </div>
      )
    }
  }

  return (
    <div className="p-4">
      <div className="flex flex-col gap-4">
        <FormProvider {...form}>
          {fields.map((pack, i) => {
            const orderItems =
              pack.items
                .map(el => currentOrder.items.find(oi => oi.id === el))
                ?.filter(Boolean)
                ?.filter(oi => oi?.type === 'product') || []
            const serverPack = packages.find(p => p.id === pack.packageId)
            const hasMovingItemsOutsideThisPackage =
              movingOrderItemIds.length > 0 && movingOrderItemIds.some(id => !orderItems.find(oi => oi.id === id))
            const isMoving = hasMovingItemsOutsideThisPackage || undefined
            return (
              <div key={pack.id} className="shadow-lg rounded-xl">
                <div className={'flex items-center justify-between gap-2 py-3 px-4 bg-blue-100 rounded-t-xl'}>
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faBoxOpen} className={'text-black'} size={'lg'} />
                    <div className={'text-black font-semibold'}>
                      {orderItems.length > 0 ? 'Balík' : 'Prázdný balík'} #{i + 1}{' '}
                      {serverPack?.carrier?.name && `- ${serverPack.carrier.name}`}
                      {serverPack?.code && ` (${serverPack.code})`}
                    </div>
                  </div>
                  <div className="gap-2 flex items-center">
                    {isMoving && (
                      <div className="flex items-center gap-2">
                        <div
                          className="flex flex-1 justify-end"
                          onClick={handleMovePressed({
                            ...pack,
                            code: serverPack?.code || '',
                            carrier: serverPack?.carrier?.id || currentOrder.carrier.id,
                            ...packageForms[pack.packageId],
                          })}>
                          <div className="bg-orange hover:bg-orange-light rounded-lg px-2 py-1 text-white flex items-center gap-2 text-sm cursor-pointer select-none">
                            <FontAwesomeIcon icon={faArrowsUpDownLeftRight} size="sm" className="text-white" />
                            Přesunout sem
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <div className="flex flex-1 justify-end" onClick={() => resetPackage(serverPack.id)}>
                        <div className="border-danger border-[1px] text-danger rounded-lg px-2 py-0.5 flex items-center gap-2 text-sm cursor-pointer select-none">
                          <FontAwesomeIcon icon={faRotateRight} size="sm" className="text-danger" />
                          Resetovat
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div
                        className="flex flex-1 justify-end"
                        onClick={() => {
                          hasTriggeredAddInitialPackage.current = false
                          const current = form.getValues().packages
                          const removedItems = current[i]?.items ?? []
                          const remaining = current.filter((_, idx) => idx !== i)
                          if (remaining.length > 0 && removedItems.length > 0) {
                            remaining[0] = {
                              ...remaining[0],
                              items: [...remaining[0].items, ...removedItems],
                            }
                          }
                          setValue('packages', remaining)
                          savePackages({ packages: remaining })
                        }}>
                        <div className="bg-danger rounded-lg px-2 py-1 text-white flex items-center gap-2 text-sm cursor-pointer select-none">
                          <FontAwesomeIcon icon={faTrash} size="sm" className="text-white" />
                          Smazat
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <PackageDetail
                  savePackages={() => savePackages(form.getValues())}
                  pack={{
                    ...pack,
                    code: serverPack?.code || '',
                    carrier: serverPack?.carrier?.id || currentOrder?.carrier?.id,
                    ...packageForms[pack.packageId],
                  }}
                  index={i}
                  onMovePressed={handleMovePressed({
                    ...pack,
                    code: serverPack?.code || '',
                    carrier: serverPack?.carrier?.id || currentOrder?.carrier?.id,
                    ...packageForms[pack.packageId],
                  })}
                  onPackageChange={handlePackageChange}
                  orderItems={orderItems}
                  processStatusId={currentOrder.processStatus?.id}
                />
                {orderItems.length > 0 && (
                  <OrderItemsTable
                    items={orderItems}
                    renderActions={renderActions}
                    actionsWidth={packagesForm.length > 1 ? 230 : undefined}
                    movingOrderItemIds={movingOrderItemIds}
                    packaging={currentOrder.processStatus?.id === 'packing'}
                  />
                )}
              </div>
            )
          })}
        </FormProvider>
      </div>
      <div className={'flex gap-2 py-4'}>
        <Button variant="outline" onClick={() => addPackage()} icon={['far', 'plus']} disabled={isSaving}>
          {'Přidat balík'}
        </Button>
        <Button
          variant="outline"
          onClick={() => addPackages()}
          icon={['far', 'rectangle-history-circle-plus']}
          disabled={isSaving}>
          {'Přidat vice balíků'}
        </Button>
        {/* The button is debug-only. It must not be available in the normal flow */}
        {/* {(form.formState.isDirty || Object.keys(packageForms).length > 0) && (
          <Button
            variant="outline"
            onClick={handleSubmit(d => savePackages(d))}
            disabled={isSaving}
            icon={['fas', 'floppy-disk']}>
            {'Uložit'}
          </Button>
        )} */}
      </div>
    </div>
  )
}

function useSavePackages({
  packageForms,
  orderId,
  onSavingChange,
}: {
  packageForms: PackageFormData
  orderId: number
  onSavingChange: (saving: boolean) => void
}) {
  const queryClient = useQueryClient()
  const context = useOrderDetailContext()
  const currentOrder = useFragment(OrderItemsInPackages_OrderFragment, context.order)
  const packages = currentOrder.packages || []

  const createNewPackage = useGQLFetcher(createNewPackageMutation)
  const deletePackage = useGQLFetcher(deletePackageMutation)
  const updatePackage = useGQLFetcher(updatePackageMutation)

  return useMutation({
    onMutate: () => {
      onSavingChange(true)
    },
    mutationFn: async (submittedForm: FromData) => {
      const newPackages = submittedForm.packages.filter(p => p.packageId < 0)
      const removedPackages = packages.filter(p => !submittedForm.packages.find(fp => fp.packageId === p.id))
      const packagesToUpdate = submittedForm.packages.filter(fp => {
        if (fp.packageId < 0) {
          return false
        }
        const serverPackage = packages.find(p => p.id === fp.packageId)
        // Check if order items in package is different form server state
        const areItemsChanged = fp.items.some(el => !serverPackage?.items.map(oi => oi.id).includes(el))
        const packageForm = packageForms[fp.packageId]
        const arePrimitivesChanged =
          Object.keys(
            compareObjectPrimitives({ ...fp, ...packageForm }, serverPackage, [
              'tariff',
              'weight',
              'dimensionX',
              'dimensionY',
              'dimensionZ',
            ]),
          ).length > 0
        return areItemsChanged || arePrimitivesChanged
      })

      const promises = []

      // 1. Save all new packages
      if (newPackages.length > 0) {
        promises.push(
          toastPromise(
            Promise.all(
              newPackages.map(p => {
                const packageForm = packageForms[p.packageId]
                return createNewPackage({
                  order: currentOrder.id,
                  items: p.items,
                  carrier: currentOrder?.carrier?.id || null,
                  ...(packageForm || {}),
                })
              }),
            ),
            {
              pending: 'Vytvářím balíky...',
              success: 'Balíky vytvořeny',
              error: 'Chyba při vytváření balíků',
            },
          ),
        )
      }

      // 2. Remove all removed packages
      if (removedPackages.length > 0) {
        promises.push(
          toastPromise(
            handlePromiseAllToast(
              removedPackages.map(p => {
                return deletePackage({ id: p.id })
              }),
            ),
            {
              pending: 'Mažu balíky...',
              success: 'Balíky smazány',
              error: 'Chyba při mazání balíků',
            },
          ),
        )
      }

      // 2.5. In deletition we need to make real optimistic update to ensure that we will not send delete request twice
      queryClient.setQueryData(['order', { id: orderId }], (old: any) => {
        const oldPackages = old?.order?.data?.packages || []

        const optimisticPackages = oldPackages.filter(p => !removedPackages.find(rp => rp.id === p.id))

        if (old) {
          return {
            order: {
              ...old.order,
              data: {
                ...old.order.data,
                packages: optimisticPackages,
              },
            },
          }
        }
      })

      // 3. Update all packages that have changed
      if (packagesToUpdate.length > 0) {
        promises.push(
          toastPromise(
            Promise.all(
              packagesToUpdate.map(fp => {
                const serverPackage = packages.find(p => p.id === fp.packageId)
                const packageForm = packageForms[fp.packageId]
                return updatePackage({
                  id: fp.packageId,
                  items: fp.items,
                  ...(packageForm?.tariff &&
                  serverPackage?.tariff?.id &&
                  packageForm.tariff !== serverPackage?.tariff?.id
                    ? { tariff: packageForm.tariff }
                    : {}),
                  ...compareObjectPrimitives({ ...fp, ...packageForm }, serverPackage, [
                    'weight',
                    'dimensionX',
                    'dimensionY',
                    'dimensionZ',
                  ]),
                })
              }),
            ),
            {
              pending: 'Aktualizuji balíky...',
              success: 'Balíky aktualizovány',
              error: 'Chyba při aktualizaci balíků',
            },
          ),
        )
      }

      return Promise.all(promises)
    },
    onSettled: (
      data: Array<
        Array<{
          createPackage?: {
            errors: any[]
            data: {
              id: number
            }
          }
          deletePackage?: { errors: any[] }
          updatePackage?: {
            errors: any[]
            data: {
              id: number
            }
          }
        }>
      >,
      errors,
      variables,
    ) => {
      // Caution: some "optimistic" updates magic is happening below
      if (errors || !data) {
        // Can't do any "optimistic" updates here because of errors
        queryClient.invalidateQueries({ queryKey: ['order', { id: orderId }] })
        onSavingChange(false)
        return
      }

      const queryResults = data.flat()

      const removedPackages = packages.filter(p => !variables.packages.find(fp => fp.packageId === p.id))
      const newPackages = queryResults
        .map(query => {
          const [queryKey, queryData] = Object.entries(query)[0]
          if (queryKey === 'createPackage') {
            return 'data' in queryData ? queryData.data : null
          } else {
            return null
          }
        })
        .filter(Boolean)

      const updatedPackages = queryResults
        .map(query => {
          const [queryKey, queryData] = Object.entries(query)[0]

          if (queryKey === 'updatePackage') {
            return 'data' in queryData ? queryData.data : null
          } else {
            return null
          }
        })
        .filter(Boolean)

      queryClient.setQueryData(['order', { id: orderId }], (old: { order: order }) => {
        const oldPackages = old?.order?.data?.packages || [] // [1], <2>

        // Remove removed packages
        let optimisticPackages = oldPackages.filter(p => !removedPackages.includes(p))

        // Update updated packages
        optimisticPackages = optimisticPackages.map(p => {
          const formPackage = variables.packages.find(fp => fp.packageId === p.id)
          const updatedPackage = updatedPackages.find(up => up.id === p.id)

          if (formPackage && p.items.length !== formPackage.items.length && !updatedPackage) {
            // Items were moved in/out of this package, but we don't have fresh data from the server.
            // Sync with the form state: keep only items that are in the form.
            return {
              ...p,
              items: p.items.filter(pi => formPackage.items.includes(pi.id)),
            }
          }

          return updatedPackage ? updatedPackage : p
        })

        // Add new packages
        optimisticPackages = [...optimisticPackages, ...newPackages]

        if (old) {
          return {
            order: {
              ...old.order,
              data: {
                ...old.order.data,
                packages: optimisticPackages,
              },
            },
          }
        }
      })

      queryClient.invalidateQueries({ queryKey: ['order', { id: orderId }] })

      onSavingChange(false)
    },
    onError: error => {
      console.error(error)
      toastError('Chyba při zpracování balíků')
    },
  })
}
